/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Gift, Ticket, Sparkles, X, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { validateInviteCode, redeemInviteCode } from '@/lib/inviteCodes';

/**
 * Small banner shown to non-Premium patients. Reads `?invite=CODE` from the
 * URL on mount (typical flow: professional shares a `/register?invite=CODE`
 * link -> after signup, the code stays in the URL and can be redeemed here).
 *
 * Also supports manual code entry for patients who received the code via
 * WhatsApp / email without the full link.
 */
const RedeemInviteBanner = () => {
  const { user, refreshMe } = useAuth();
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [manual, setManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isPatient = user && user.category === 'paziente';
  const isPremium = user?.premium_active;

  useEffect(() => {
    const invite = params.get('invite');
    if (invite) setCode(invite.trim().toUpperCase());
  }, [params]);

  if (!isPatient || isPremium || dismissed) return null;

  const doRedeem = async (theCode) => {
    if (!theCode) return;
    setBusy(true);
    try {
      // Validate first for a nicer error UX
      const v = await validateInviteCode(theCode);
      if (!v.valid) {
        const reasonLabel = {
          not_found: 'Codice non valido',
          already_redeemed: 'Codice già utilizzato',
          expired: 'Codice scaduto',
        }[v.reason] || 'Codice non valido';
        toast.error(reasonLabel);
        return;
      }
      const res = await redeemInviteCode(theCode);
      toast.success(`Premium attivato per ${res.grant_days} giorni! 🎉`);
      // Clean the ?invite= from the URL
      params.delete('invite');
      setParams(params, { replace: true });
      setCode('');
      setManual(false);
      // Refresh the user profile so isPremium hides the banner immediately
      await refreshMe?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel riscatto');
    } finally {
      setBusy(false);
    }
  };

  const hasIncomingCode = !!code;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        data-testid="redeem-invite-banner"
      >
        <Card className="border-primary/40 bg-primary-gradient/[0.06]">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Gift className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-sm flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  {hasIncomingCode
                    ? `Codice invito rilevato: ${code}`
                    : 'Hai un codice invito da un professionista?'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sblocca 90 giorni di Premium gratis su SanitaNexus.
                </p>
                {(hasIncomingCode || manual) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="CODICE"
                      className="w-40 font-mono font-bold tracking-widest uppercase"
                      maxLength={16}
                      data-testid="redeem-invite-input"
                    />
                    <Button
                      size="sm"
                      onClick={() => doRedeem(code)}
                      disabled={busy || !code.trim()}
                      data-testid="redeem-invite-submit"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Riscatta
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                {!manual && !hasIncomingCode && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setManual(true)}
                    data-testid="redeem-invite-open"
                  >
                    <Ticket className="h-3.5 w-3.5 mr-1" /> Inserisci
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setDismissed(true)}
                  data-testid="redeem-invite-dismiss"
                  aria-label="Chiudi"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default RedeemInviteBanner;
